import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Download, ChevronDown, Search } from 'lucide-react';
import { useTithes } from '../hooks/useTithes';
import { TithesAnnualMatrix, TithesLegend } from '../components/TithesAnnualMatrix';
import { TithesSummary } from '../components/TithesSummary';
import { exportTithesExcel } from '../utils/exportTithes';
import type { TitheFilters, TitheScope } from '../types';
import { getAllDistrictsWithRegions } from '@/Services/regions.service';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import { useUserProfile } from '@/features/credential-requests/hooks/useUserProfile';
import { useActiveBankAccounts } from '@/features/settings/hooks/useBankAccounts';
import { getAvailableYears } from '@/lib/dateUtils';

const SCOPE_LABELS: Record<TitheScope, string> = {
  NATIONAL:    'Directorio Nacional',
  DISTRICT:    'Distritos',
  REGION:      'Regiones',
  INSTITUTION: 'Instituciones',
};

const SELECT_CLASS =
  'appearance-none rounded-md border border-input bg-background py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

/** Accent- and case-insensitive form for client-side search. */
const normalize = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export function TithesPage() {
  const currentYear = new Date().getFullYear();

  const [exporting, setExporting] = useState(false);

  // Filters live in the URL so reload / back / shared links keep the view.
  const [params, setParams] = useSearchParams();
  const yearParam  = parseInt(params.get('year') ?? '', 10);
  const scopeParam = params.get('scope') as TitheScope | null;
  const year       = yearParam >= 2021 && yearParam <= currentYear + 1 ? yearParam : currentYear;
  const scope: TitheScope = scopeParam && scopeParam in SCOPE_LABELS ? scopeParam : 'NATIONAL';
  const districtId = params.get('districtId');
  const regionId   = params.get('regionId');
  const query      = params.get('q') ?? '';

  // Primitive deps keep `filters` referentially stable — useTithes reloads when it changes.
  const filters = useMemo<TitheFilters>(
    () => ({ year, scope, districtId, regionId }),
    [year, scope, districtId, regionId],
  );

  const updateParams = (patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v) next.set(k, v); else next.delete(k);
      }
      return next;
    }, { replace: true });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTithesExcel(filters.year);
    } finally {
      setExporting(false);
    }
  };

  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions]     = useState<Region[]>([]);
  const { isAdmin: userIsAdmin, isLoading: profileLoading } = useUserProfile();
  const { data: bankAccounts = [] } = useActiveBankAccounts();

  // Load territories for filter dropdowns
  useEffect(() => {
    getAllDistrictsWithRegions().then((dists) => {
      setDistricts(dists);
      const allRegions = dists.flatMap((d) => (d.regions ?? []) as Region[]);
      setRegions(allRegions);
    }).catch(console.error);
  }, []);

  const { matrix, isLoading, error, reload } = useTithes(filters);

  const filteredMatrix = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return matrix;
    return matrix
      .map((g) => ({
        ...g,
        rows: g.rows.filter((r) => normalize(`${r.label} ${r.sublabel} ${r.roleLabel}`).includes(q)),
      }))
      .filter((g) => g.rows.length > 0);
  }, [matrix, query]);

  const availableYears = getAvailableYears(currentYear);

  const handleScopeChange = (next: TitheScope) => {
    updateParams({ scope: next === 'NATIONAL' ? null : next, districtId: null, regionId: null });
  };

  const regionsForDistrict = filters.districtId
    ? regions.filter((r) => r.district_id === filters.districtId)
    : regions;

  const showDistrictPicker = filters.scope === 'REGION';
  const showRegionPicker   = filters.scope === 'REGION';
  const loadingInitial     = (isLoading && matrix.length === 0) || profileLoading;

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="border-b border-border bg-card p-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Diezmos</h1>
            <p className="text-sm text-muted-foreground">Registro anual de diezmos por autoridad y entidad</p>
          </div>
          <button
            onClick={handleExport}
            disabled={isLoading || exporting || matrix.length === 0}
            className="flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Year */}
          <div className="relative">
            <select
              value={filters.year}
              onChange={(e) => updateParams({ year: e.target.value === String(currentYear) ? null : e.target.value })}
              className={SELECT_CLASS}
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          {/* Scope — segmented on desktop, select on phones */}
          <div className="hidden overflow-hidden rounded-md border border-input text-sm sm:flex">
            {(Object.keys(SCOPE_LABELS) as TitheScope[]).map((s) => (
              <button
                key={s}
                onClick={() => handleScopeChange(s)}
                className={`px-3 py-1.5 transition-colors ${
                  filters.scope === s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                }`}
              >
                {SCOPE_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="relative sm:hidden">
            <select
              value={filters.scope}
              onChange={(e) => handleScopeChange(e.target.value as TitheScope)}
              className={SELECT_CLASS}
              aria-label="Ámbito"
            >
              {(Object.keys(SCOPE_LABELS) as TitheScope[]).map((s) => (
                <option key={s} value={s}>{SCOPE_LABELS[s]}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          {/* District picker */}
          {showDistrictPicker && (
            <div className="relative">
              <select
                value={filters.districtId ?? ''}
                onChange={(e) => updateParams({ districtId: e.target.value || null, regionId: null })}
                className={SELECT_CLASS}
              >
                <option value="">Todos los distritos</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          )}

          {/* Region picker */}
          {showRegionPicker && (
            <div className="relative">
              <select
                value={filters.regionId ?? ''}
                onChange={(e) => updateParams({ regionId: e.target.value || null })}
                className={SELECT_CLASS}
              >
                <option value="">Todas las regiones</option>
                {regionsForDistrict.map((r) => (
                  <option key={r.id} value={r.id}>{r.code} — {r.name}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          )}

          {/* Search (client-side, over the rows already loaded) */}
          <div className="relative w-full sm:ml-auto sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => updateParams({ q: e.target.value || null })}
              placeholder="Buscar por nombre o código"
              className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      {/* Content — the matrix owns the scroll so its header and name columns can stick */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4 sm:overflow-hidden sm:p-6">
        {loadingInitial ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-destructive">
            {error}
            <button onClick={reload} className="ml-2 underline">Reintentar</button>
          </div>
        ) : (
          <>
            {matrix.length > 0 && <TithesSummary groups={matrix} year={filters.year} />}
            {matrix.length > 0 && <TithesLegend />}
            <div className="flex min-h-96 flex-1 flex-col sm:min-h-0">
              <TithesAnnualMatrix
                groups={filteredMatrix}
                year={filters.year}
                isAdmin={userIsAdmin}
                bankAccounts={bankAccounts}
                onUpdated={reload}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
